/**
 * devices/ExitLadder.tsx — THE WAY OUT. Rendered under the primary action in
 * EVERY state of the device gate, without exception.
 *
 * ── WHY THIS IS ITS OWN COMPONENT ─────────────────────────────────────────
 * A 401 classifier once raised a focus-trapping overlay whose only control
 * signed the user out. They signed back in, repeated the action, and hit the
 * same wall — an infinite loop for a real population of users. The lesson is
 * not "be careful": it is that the exits must be a COMPONENT that every state
 * renders, so a future state cannot be added without them. If you are adding a
 * fourth gate state and you did not render this, the state is not finished.
 *
 * The ladder, in order of how likely it is to be the right answer:
 *
 *   1  (the caller's primary — evict the suggested session, or sign in again)
 *   2  Sign out my other sessions   — the recovery for "I lost the laptop and
 *                                     I can't tell which of these rows it is".
 *                                     Works from a device that holds NO slot,
 *                                     because the devices router sets
 *                                     `deviceSelfService` (functions/src/
 *                                     devices/index.ts:69). Nothing in the
 *                                     product offered this before.
 *   3  Trouble getting in?          — a real human, pre-filled with the ids
 *                                     support needs to run
 *                                     `POST /admin/users/:uid/devices/reset`.
 *                                     This is the ONLY exit that survives every
 *                                     failure, including an empty or malformed
 *                                     409 body, so it is present in all states.
 *   4  Not now                      — leave. Shell-aware, and HONEST about what
 *                                     it does on each shell (see below).
 *
 * ── EXIT 4 IS NOT THE SAME PROMISE ON BOTH SHELLS ─────────────────────────
 * `docs/ORG-FRONTEND-PLAN.md` §4 says "Not now — browse without signing in.
 * Always present." That is keepable on desktop, where anonymous browsing is
 * real (locked prices, CLAUDE.md invariant 3). It is NOT keepable in the mobile
 * app shell: `MobileApp.tsx`'s PUBLIC_PATHS is {welcome, signin, register,
 * terms, privacy, refunds} — there is nothing to browse signed out. Writing
 * "browse without signing in" there would be a lie printed on the one screen
 * where a user has already lost trust, so the phone gets a plain "Not now" and
 * a sentence saying exactly what will happen. The loop-breaker on that shell is
 * exit 2 (which fixes the account) plus the "Trouble signing in?" link now on
 * the Welcome screen.
 */
import { PressScale } from '../../lib/PressScale'
import { isAppMode } from '../../mobile/lib/appMode'
import type { DeviceSummary } from '../../lib/deviceSession'

const SUPPORT_EMAIL = 'info@thediaex.com'

/**
 * The mailto support falls back to. It carries the ids a human needs to run the
 * admin device reset, so the user never has to describe a device id over email.
 */
export function troubleMailto(opts: {
  uid?: string | null
  deviceId?: string | null
  devices?: DeviceSummary[]
  subject?: string
}): string {
  const lines = [
    'I can’t get past the device limit on Diaex.',
    '',
    '— Please keep the details below, they help support fix this —',
    `Account: ${opts.uid || '(not signed in)'}`,
    `This device: ${opts.deviceId || '(unknown)'}`,
    ...(opts.devices?.length
      ? ['Listed sessions:', ...opts.devices.map((d) => `  · ${d.label} — ${d.deviceId}`)]
      : ['Listed sessions: (none were shown)']),
  ]
  const subject = opts.subject ?? 'Can’t sign in — device limit'
  return `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(lines.join('\n'))}`
}

export function ExitLadder({
  devices, uid, deviceId, busy, onSignOutOthers, onNotNow, appMode = isAppMode(),
}: {
  devices: DeviceSummary[]
  uid: string | null
  deviceId: string | null
  busy: boolean
  /** Omitted on the states where the session is already dead and revoke would 403. */
  onSignOutOthers?: () => void
  onNotNow: () => void
  /** Injectable for tests; defaults to the live shell check. */
  appMode?: boolean
}) {
  return (
    <div className="mt-3 flex flex-col gap-1.5">
      {onSignOutOthers && (
        <PressScale
          feel="dim"
          className="btn-secondary w-full"
          disabled={busy}
          onClick={onSignOutOthers}
          data-testid="device-gate-exit-all"
        >
          Sign out my other sessions
        </PressScale>
      )}
      <a
        href={troubleMailto({ uid, deviceId, devices })}
        // `.btn-ghost` is h-10. Every control on a BLOCKING screen is a 44px
        // target, so the exits floor themselves rather than inheriting 40.
        className="btn-ghost min-h-11 w-full"
        data-testid="device-gate-exit-help"
      >
        Trouble getting in?
      </a>
      <PressScale
        feel="dim"
        className="btn-ghost min-h-11 w-full"
        onClick={onNotNow}
        data-testid="device-gate-exit-not-now"
      >
        {appMode ? 'Not now' : 'Not now — browse without signing in'}
      </PressScale>
      {appMode && (
        <p className="px-2 text-center text-[11px] leading-snug text-navy-500/90">
          You’ll be signed out on this device. Your other sessions stay signed in.
        </p>
      )}
    </div>
  )
}
